import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../../../hooks/AuthHooks";
import { Fragment, useCallback, useMemo, useRef, useState } from "react";
import { notifyError, notifyPending } from "../../../../utils/notify";
import Form from "../../../../components/Base/Form";
import Select from "../../../../components/Base/Select";
import Input from "../../../../components/Base/Input";
import ButtonBar from "../../../../components/Base/ButtonBar";
import Button from "../../../../components/Base/Button";
import Modal from "../../../../components/Base/Modal";
import PaymentSummary from "../../../../components/Compound/PaymentSummary";
import ElectronicTallyTicket
  from "../components/TicketsBancoAgrario/TicketsElectronicTally/ElectronicTallyTicket";
import { useReactToPrint } from "react-to-print";
import { useFetch } from "../../../../hooks/useFetch";
import { fetchCustom } from "../../../RecaudoGenericoPDP/utils/fetchRecaudoGenerico";

const URL_REGISTER_ELECTRONIC_TALLY = `${process.env.REACT_APP_URL_BANCO_AGRARIO}/banco-agrario/electronic-tally/register`


export const processTypeOptions =  [
  { value: "CONTAINER", label: "CONTENEDOR" },
  { value: "DOCUMENT_TRAFFICKING", label: "TRÁFICO DE DOCUMENTOS" },
];

export const getLabelByValue = (value) => {
  const option = processTypeOptions.find(opt => opt.value === value);
  return option?.label;
};

const RegisterElectronicTally = () =>{
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    processType: "CONTAINER",
    movementDate: "",
    shippingDate: "",
    containerStickerNumber: "",
    sealNumber: "",
    tulaNumber: "",
    observations: "",
  });
  const { roleInfo, pdpUser } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [summary, setSummary] = useState({});
  const [statusStep, setStatusStep] = useState(0);
  const [ticketObject, setTicketObject] = useState({});
  const printDiv = useRef();
  const handlePrint = useReactToPrint({
    content: () => printDiv.current,
  });
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    if (type === "date") {
      const newFormData = {
        ...formData,
        [name]: value,
      };

      const movement = newFormData.movementDate;
      const shipping = newFormData.shippingDate;
      if (movement && shipping && new Date(movement) > new Date(shipping)) {
        notifyError("La fecha de movimiento no puede ser posterior a la de envío");
        return;
      }

      setFormData(newFormData);
      return;
    }
    if (type === "select-one") {
      setFormData((prev) => ({ ...prev, [name]: value }));
      return;
    }
    const alphanumericRegex = /^[a-zA-Z0-9]*$/;
    if (!alphanumericRegex.test(value)) {
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleElectronicTally = useCallback((e) => {
    e.preventDefault();

    const data = {
      "Tipo de proceso": getLabelByValue(formData.processType) ?? "No definido",
      "Fecha de movimiento": formData.movementDate ?? "No especificada",
      "Fecha de envío": formData.shippingDate ?? "No especificada",
      "No. Sticker": formData.containerStickerNumber ?? "No especificado",
      "No. Precinto": formData.sealNumber ?? "No especificado",
      "No. Tula": formData.tulaNumber ?? "No especificado",
      "Observaciones": formData.observations ?? "Ninguna",
    };

    setSummary(data);
    setStatusStep(1);
    setShowModal(true);
  }, [formData]);


  const [
    loadingRegisterElectronicTally,
    registerElectronicTally,
  ] = useFetch(
    fetchCustom(
      URL_REGISTER_ELECTRONIC_TALLY,
      "POST",
      "registro de punteo electrónico"
    )
  );
  const registerElectronic = useCallback(
    (ev) => {

      ev.preventDefault();
      const data = {
        comercio:{
            id_comercio: roleInfo?.id_comercio ?? "",
            commerce_name: roleInfo?.["nombre comercio"] ?? "",
            user_name: pdpUser?.uname ?? "",
            id_usuario: roleInfo?.id_usuario ?? "",
            commerce_address: roleInfo?.direccion ?? "",
            id_terminal: roleInfo?.["id_dispositivo"] ?? "",
        },
        process_type: formData.processType ?? "No especificada",
        movement_date: formData.movementDate ?? "No especificada",
        shipping_date: formData.shippingDate ?? "No especificada",
        container_sticker_number: formData.containerStickerNumber ?? "No especificado",
        seal_number: formData.sealNumber ?? "No especificado",
        tula_number: formData.tulaNumber ?? "No especificado",
        observations: formData.observations ?? "Ninguna",
      };

      notifyPending(
        registerElectronicTally(data, data),
        {
          render: () => {
            return "Procesando registro";
          },
        },
        {
          render: ({ data: res }) => {
            setTicketObject(res?.obj?.ticket);
            setStatusStep(2);
            setShowModal(true);
            return "Registro satisfactorio";
          },
        },
        {
          render: ({ data: error }) => {
            navigate("/GestionTransaccional/punteo-electronico");
            return error?.message ??"Registro fallido";
          },
        }
      );
    },
    [formData, roleInfo, pdpUser]
  );
  const handleCancel = useCallback(() => {
    setShowModal(false);
    navigate("/GestionTransaccional/punteo-electronico");
    notifyError("Transacción cancelada por el usuario");
  }, [navigate]);
  const handleClose = useCallback(() => {
    setShowModal(false);
    navigate("/GestionTransaccional/punteo-electronico");
  }, [navigate]);

  const hasData = useMemo(() => {
    if (!roleInfo || (roleInfo && Object.keys(roleInfo).length === 0)) {
      return false;
    }
    const keys = [
      "id_comercio",
      "id_usuario",
      "tipo_comercio",
      "id_dispositivo",
      "ciudad",
      "direccion",
      "codigo_dane",
    ];
    for (const key of keys) {
      if (!(key in roleInfo)) {
        return false;
      }
    }
    return true;
  }, [roleInfo]);

  if (!hasData) {
    notifyError(
      "El usuario no cuenta con datos de comercio, no se permite la transacción"
    );
    return <Navigate to={"/"} replace />;
  }

  return (
    <Fragment>
      <h1 className="text-3xl mt-6">Punteo Electrónico</h1>
      <Form onSubmit={handleElectronicTally} grid>
        <Input
          id="commerceId"
          name="commerceId"
          label="ID comercio"
          value={roleInfo?.id_comercio}
          readOnly={true}
        />
        <Input
          id="commerceName"
          name="commerceName"
          label="Nombre Comercio"
          value={roleInfo?.["nombre comercio"]}
          readOnly={true}
        />
        <Select
          id="processType"
          name="processType"
          label="Tipo de proceso"
          options={processTypeOptions}
          value={formData.processType}
          onChange={handleInputChange}
          required
        />
        <Input
          id="movementDate"
          name="movementDate"
          type="date"
          label="Fecha de movimiento"
          value={formData.movementDate}
          onChange={handleInputChange}
          required
        />
        <Input
          id="shippingDate"
          name="shippingDate"
          type="date"
          label="Fecha de envío"
          value={formData.shippingDate}
          onChange={handleInputChange}
          required
        />
        <Input
          id="containerStickerNumber"
          name="containerStickerNumber"
          label="Número Sticker Contenedor"
          minLength={16}
          maxLength={16}
          value={formData.containerStickerNumber}
          onChange={handleInputChange}
          required
        />
        <Input
          id="sealNumber"
          name="sealNumber"
          label="Número Precinto"
          minLength={10}
          maxLength={10}
          value={formData.sealNumber}
          onChange={handleInputChange}
          required
        />
        <Input
          id="tulaNumber"
          name="tulaNumber"
          label="Número de Tula"
          minLength={16}
          maxLength={16}
          value={formData.tulaNumber}
          onChange={handleInputChange}
          required
        />
        <Input
          id="observations"
          name="observations"
          label="Observaciones"
          minLength={0}
          maxLength={30}
          value={formData.observations}
          onChange={handleInputChange}
          required
        />

        <ButtonBar className="lg:col-span-2 flex gap-x-2">
          <Button type="submit">Realizar punteo</Button>
          <Button onClick={handleCancel}>
            Cancelar
          </Button>
        </ButtonBar>
      </Form>
      {statusStep === 1 && (
      <Modal show={showModal} handleClose={loadingRegisterElectronicTally ? () => {} : handleCancel}>

          <PaymentSummary summaryTrx={summary}>
            <ButtonBar>
              <Button
                type="submit"
                onClick={registerElectronic}
                disabled={loadingRegisterElectronicTally}
              >
                Aceptar
              </Button>
              <Button onClick={handleCancel} disabled={loadingRegisterElectronicTally}>
                Cancelar
              </Button>
            </ButtonBar>
          </PaymentSummary>


      </Modal>)}
      {statusStep === 2 && (
      <Modal show={showModal} handleClose={loadingRegisterElectronicTally ? () => {} : handleClose}>
          <div className="flex flex-col items-center gap-4">
            <ElectronicTallyTicket refPrint={printDiv} ticket={ticketObject} />
            <ButtonBar>
              <Button onClick={handlePrint}>Imprimir</Button>
              <Button onClick={handleClose}>Cerrar</Button>
            </ButtonBar>
          </div>
      </Modal>)}
    </Fragment>
  );
};
export default RegisterElectronicTally;